/**
 * Screenshot capture utility for Ask Advisor.
 *
 * Captures a DOM element as a WebP image blob by:
 * 1. Rendering the element to PNG via domtoimage (same library used by Save/Copy Image)
 * 2. Converting the PNG to WebP via the Canvas API
 *
 * IMPORTANT: This utility is ONLY used for the Ask Advisor flow.
 * The existing Save Image / Copy Image features remain unchanged and
 * continue to use domtoimage.toBlob() directly (PNG output).
 */

import domtoimage from 'src/utils/dom-to-imagev2';
import { defaultImageOption } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/hooks/useCsvExporter';

/**
 * Capture a DOM element as a WebP blob for upload to S3.
 *
 * @param element - The DOM element to capture (e.g., chartContainerRef.current)
 * @param quality - WebP quality 0–1 (default 1, good balance of quality vs size)
 * @returns WebP Blob ready for upload
 */
export async function captureElementAsWebp(element: HTMLElement, quality = 1): Promise<Blob> {
  // 1. Capture as PNG via domtoimage (reuses existing export logic & scale settings)
  //    Use a filter to exclude elements marked with data-screenshot-exclude
  //    (e.g. the ellipsis export menu button).
  const pngBlob = (await domtoimage.toBlob(element, {
    ...defaultImageOption,
    filter: (node: Node) => {
      if (node instanceof HTMLElement && node.hasAttribute('data-screenshot-exclude')) {
        return false;
      }
      return true;
    }
  })) as Blob;

  if (!pngBlob) {
    throw new Error('domtoimage.toBlob returned null');
  }

  // 2. Convert PNG → WebP via Canvas API
  return convertBlobToWebp(pngBlob, quality);
}

/**
 * Convert an image blob (PNG) to WebP format using the Canvas API.
 */
function convertBlobToWebp(pngBlob: Blob, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(pngBlob);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas 2d context'));
          return;
        }
        ctx.drawImage(img, 0, 0);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('canvas.toBlob returned null for WebP conversion'));
            }
          },
          'image/webp',
          quality
        );
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load PNG blob for WebP conversion'));
    };

    img.src = objectUrl;
  });
}
